import React from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../../lib/stripe';
import PaymentForm from './PaymentForm';
import { API_BASE_URL } from '../../api';

interface PaymentSectionProps {
  clientSecret: string;
  bookingId?: number | null;
  amountCents?: number | null;
  onSuccess?: (paymentIntentId: string) => void;
  onError?: (message: string) => void;
  returnUrl?: string;
  onBackToBooking?: () => void;
}

const PaymentSection: React.FC<PaymentSectionProps> = ({ clientSecret, bookingId, amountCents, onSuccess, onError, returnUrl, onBackToBooking }) => {
  const paymentSucceeded = React.useRef(false);
  const skipCancelRuns = React.useRef(import.meta.env.MODE !== 'production' ? 1 : 0);
  const previousBookingId = React.useRef<number | null | undefined>(undefined);

  // Scroll to top when payment section is displayed
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  React.useEffect(() => {
    if (!bookingId) {
      return;
    }

    if (previousBookingId.current !== bookingId) {
      previousBookingId.current = bookingId;
      skipCancelRuns.current = import.meta.env.MODE !== 'production' ? 1 : 0;
    }

    // Cette fonction de nettoyage sera appelée lorsque le composant est démonté
    return () => {
      if (skipCancelRuns.current > 0) {
        skipCancelRuns.current -= 1;
        return;
      }

      if (!paymentSucceeded.current) {
        // Annuler la réservation ET le PaymentIntent
        fetch(`${API_BASE_URL}/api/bookings/${bookingId}/cancel?cancel_payment_intent=true`, {
          method: 'PATCH',
        }).catch(err => {
          console.error("Erreur lors de l'annulation de la réservation:", err);
        });
      }
    };
  }, [bookingId]);
  if (!clientSecret) return null;

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: { theme: 'stripe' },
        locale: 'fr',
      }}
    >
            <PaymentForm 
        onSuccess={(paymentIntentId) => {
          paymentSucceeded.current = true;
          onSuccess?.(paymentIntentId);
        }}
        onError={onError} 
        amountCents={amountCents ?? undefined} 
        returnUrl={returnUrl}
        onBackToBooking={onBackToBooking}
      />
    </Elements>
  );
};

export default PaymentSection;
